import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>;
  children: ReactNode;
  active?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "accent" | "ghost";
}

const sizeClasses = {
  sm: "w-7 h-7 text-sm",
  md: "w-9 h-9 text-base",
  lg: "w-11 h-11 text-lg",
};

/**
 * 28 px (`sm`) et 36 px (`md`), c'est la bonne densité à la souris et deux
 * cibles qu'on rate au doigt. On étend la zone sensible par un pseudo-élément
 * débordant plutôt qu'en agrandissant le bouton : la toolbar tient déjà tout
 * juste en largeur sur un téléphone en paysage, et grossir le glyphe casserait
 * la densité desktop.
 *
 * Le débord vertical comble exactement l'écart aux 44 px recommandés : −8 px
 * pour `sm`, −4 px pour `md`. Horizontalement on se limite à −2 px sur `sm` et
 * à rien sur `md` : l'atome est utilisé dans toute l'application, y compris
 * dans des groupes serrés comme le transport, où déborder ferait recouvrir la
 * cible voisine. `pointer-coarse` n'existe pas au pointeur fin : rien ne bouge
 * au clavier-souris.
 *
 * `lg` vaut déjà 44 px et n'a donc rien à rattraper.
 */
const COARSE_HIT_AREA_BASE =
  "relative pointer-coarse:after:absolute pointer-coarse:after:content-['']";

const coarseHitArea: Record<NonNullable<IconButtonProps["size"]>, string> = {
  sm: `${COARSE_HIT_AREA_BASE} pointer-coarse:after:-inset-y-2 pointer-coarse:after:-inset-x-0.5`,
  md: `${COARSE_HIT_AREA_BASE} pointer-coarse:after:-inset-y-1`,
  lg: "",
};

export function IconButton({
  ref,
  children,
  active = false,
  size = "md",
  variant = "default",
  className = "",
  ...props
}: IconButtonProps) {
  const base =
    "inline-flex items-center justify-center rounded-lg transition-all duration-150 cursor-pointer select-none";

  const variants = {
    default: active
      ? "bg-accent/20 text-accent-bright hover:bg-accent/30"
      : "bg-bg-elevated text-text-secondary hover:bg-bg-hover hover:text-text-primary",
    accent:
      "bg-accent text-white hover:bg-accent-bright active:bg-accent-dim",
    ghost: active
      ? "text-accent-bright hover:bg-accent/10"
      : "text-text-secondary hover:text-text-primary hover:bg-bg-elevated",
  };

  const hitArea = coarseHitArea[size];

  return (
    <button
      ref={ref}
      className={`${base} ${sizeClasses[size]} ${hitArea} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
